import React, { useEffect, useCallback, useRef, use } from 'react';
import useDebounce from '../../utils/useDebounce';
import { PageHeader } from '../../components';
import styles from './AddProducts.module.scss';
import { Button, Col, Form, Row, Card, Alert } from 'react-bootstrap';
import { toast } from 'react-toastify';
import {
    PRODUCT_FORM_FIELDS as FORM_FIELDS,
    INITIAL_FORM_DATA,
    buildPayload,
    validateForm,
    DEBOUNCE_DELAY
} from './utils/AddProductsConstants';
import { connect } from 'react-redux';
import { getVoucherThemes, searchVoucherThemeDropdownList, voucherThemeSearchApi } from '../../api/voucherTheme';
import { getAllMerchantGroupApi, getMerchantGroupsBySearch, searchMerchantDropdownList } from '../../api/merchant';
import { getAddOns, searchAddOnDropdownList } from '../../api/Vouchers';
import { addProduct, getProductCategories, searchProductCategories, searchProductCategoryDropdownList } from '../../api/Products';
import CategorySelect from './utils/CategorySelect';
import VoucherThemeSelect from './utils/VoucherThemeSelect';
import AddOnsSelect from './utils/AddOnsSelect';
import MerchantGroupSelect from './utils/MerchantGroupSelect';
import OptionsSection from './utils/OptionsSection';
import useComponentState from '../../utils/useComponentState';
import ClientSelect from './utils/ClientSelect';
import { searchClientDropdownList } from '../../api/Client';


const AddProducts = ({ auth }) => {
    const [state, setState] = useComponentState({
        formData: { ...INITIAL_FORM_DATA },
        categories: [],
        voucherThemes: [],
        addOns: [],
        merchantGroups: [],
        clients: [],
        loadingClients: false,
        clientSearchTerm: '',
        clientSearchType: 'name',
        loadingMerchantGroups: false,
        loadingVoucherThemes: false,
        loadingCategories: false,
        loadingAddOns: false,
        merchantGroupSearchTerm: '',
        categorySearchTerm: '',
        voucherThemeSearchTerm: '',
        merchantGroupSearchType: 'id',
        categorySearchType: 'name',
        voucherThemeSearchType: 'name',
        validated: false,
        error: '',
        loading: false,
        fieldErrors: {}
    });

    // Track latest request ID for each dropdown separately
    const latestMerchantGroupReqIdRef = useRef(0);
    const latestCategoryReqIdRef = useRef(0);
    const latestVoucherThemeReqIdRef = useRef(0);
    const latestClientReqIdRef = useRef(0);

    const abortCtrlRef = useRef(null);
    const categoryAbortCtrlRef = useRef(null);
    const voucherThemeAbortCtrlRef = useRef(null);
    const clientAbortCtrlRef = useRef(null);

    // Debounce the search terms coming from the UI
    const debouncedMerchantGroupSearchTerm = useDebounce(state.merchantGroupSearchTerm, DEBOUNCE_DELAY);
    const debouncedCategorySearchTerm = useDebounce(state.categorySearchTerm, DEBOUNCE_DELAY);
    const debouncedVoucherThemeSearchTerm = useDebounce(state.voucherThemeSearchTerm, DEBOUNCE_DELAY);
    const debouncedClientSearchTerm = useDebounce(state.clientSearchTerm, DEBOUNCE_DELAY);

    // Fetch add-ons
    const fetchAddOns = useCallback(async () => {
        setState(prevState => ({ ...prevState, loadingAddOns: true }));
        try {
            const response = await searchAddOnDropdownList(auth.auth.token, '', '', 'name', 'asc');
            if (response) {
                setState(prevState => ({ ...prevState, addOns: response.addOns || [] }));
            }
        } catch (error) {
            toast.error('Failed to fetch add-ons');
        } finally {
            setState(prevState => ({ ...prevState, loadingAddOns: false }));
        }
    }, [auth.auth.token]);

    const fetchMerchantGroupsWithSearch = useCallback(async (term, searchType, token) => {
        // abort previous
        if (abortCtrlRef.current) abortCtrlRef.current.abort();

        const reqId = ++latestMerchantGroupReqIdRef.current;
        const controller = new AbortController();
        abortCtrlRef.current = controller;

        setState(prev => ({ ...prev, loadingMerchantGroups: true }));

        try {
            let response;

            // With search term → perform filtered search
            response = await searchMerchantDropdownList(token, term, searchType || 'id', "name", "asc", { signal: controller.signal });

            // ignore stale responses
            if (reqId !== latestMerchantGroupReqIdRef.current) return;

            let defaultId = null;
            if (response?.merchantGroups.length !== 0) {
                defaultId = response.merchantGroups.find(mg => mg.name.toLowerCase() === 'all');
            }
            setState(prev => ({ ...prev, merchantGroups: response?.merchantGroups || [], loadingMerchantGroups: false, formData: { ...prev.formData, selectedMerchantGroupId: defaultId?.id || '' } }));
        } catch (err) {
            if (reqId !== latestMerchantGroupReqIdRef.current) return;
            if (err?.name === 'AbortError') return; // request cancelled

            toast.error('Failed to search merchant groups');
            setState(prev => ({ ...prev, loadingMerchantGroups: false }));
        }
    }, []);

    // Fetch categories with search
    const fetchCategoriesWithSearch = useCallback(async (term, searchType, token) => {
        // abort previous
        if (categoryAbortCtrlRef.current) categoryAbortCtrlRef.current.abort();

        const reqId = ++latestCategoryReqIdRef.current;
        const controller = new AbortController();
        categoryAbortCtrlRef.current = controller;

        setState(prev => ({ ...prev, loadingCategories: true }));

        try {
            let response;
            // With search term → perform filtered search
            response = await searchProductCategoryDropdownList(token, term, (searchType || 'name').toLowerCase(), "category", "asc");
            response = response?.productCategories || response || [];

            // ignore stale responses
            if (reqId !== latestCategoryReqIdRef.current) return;

            setState(prev => ({ ...prev, categories: response, loadingCategories: false }));
        } catch (err) {
            if (reqId !== latestCategoryReqIdRef.current) return;
            if (err?.name === 'AbortError') return; // request cancelled

            toast.error('Failed to search categories');
            setState(prev => ({ ...prev, loadingCategories: false }));
        }
    }, []);

    const fetchClientsWithSearch = useCallback(async (term, searchType, token) => {
        // abort previous
        if (clientAbortCtrlRef.current) clientAbortCtrlRef.current.abort();

        const reqId = ++latestClientReqIdRef.current;
        const controller = new AbortController();
        clientAbortCtrlRef.current = controller;

        setState(prev => ({ ...prev, loadingClients: true }));

        try {
            let response;
            // With search term → perform filtered search
            response = await searchClientDropdownList(token, term, searchType || "name", "clientName", "asc");
            response = response?.clients || response || [];

            // ignore stale responses
            if (reqId !== latestClientReqIdRef.current) return;

            setState(prev => ({ ...prev, clients: response, loadingClients: false }));
        } catch (err) {
            if (reqId !== latestClientReqIdRef.current) return;
            if (err?.name === 'AbortError') return; // request cancelled

            toast.error('Failed to search clients');
            setState(prev => ({ ...prev, loadingClients: false }));
        }
    }, []);

    // Fetch voucher themes with search
    const fetchVoucherThemesWithSearch = useCallback(async (term, searchType, token) => {
        // abort previous
        if (voucherThemeAbortCtrlRef.current) voucherThemeAbortCtrlRef.current.abort();

        const reqId = ++latestVoucherThemeReqIdRef.current;
        const controller = new AbortController();
        voucherThemeAbortCtrlRef.current = controller;

        setState(prev => ({ ...prev, loadingVoucherThemes: true }));

        try {
            let response;
            // With search term → perform filtered search
            response = await searchVoucherThemeDropdownList(token, term, (searchType || 'name').toLowerCase(), "name", "asc");
            response = response?.voucherThemes || response || [];

            // ignore stale responses
            if (reqId !== latestVoucherThemeReqIdRef.current) return;

            setState(prev => ({ ...prev, voucherThemes: response, loadingVoucherThemes: false }));
        } catch (err) {
            if (reqId !== latestVoucherThemeReqIdRef.current) return;
            if (err?.name === 'AbortError') return; // request cancelled

            toast.error('Failed to search voucher themes');
            setState(prev => ({ ...prev, loadingVoucherThemes: false }));
        }
    }, []);

    // Effect: fetch merchant groups when the debounced term or search type changes
    useEffect(() => {
        const term = debouncedMerchantGroupSearchTerm?.toString().trim();
        fetchMerchantGroupsWithSearch(term, state.merchantGroupSearchType, auth.auth.token);

        // cleanup when dependency changes
        return () => {
            if (abortCtrlRef.current) abortCtrlRef.current.abort();
        };
    }, [debouncedMerchantGroupSearchTerm, state.merchantGroupSearchType, auth.auth.token, fetchMerchantGroupsWithSearch]);

    // Effect: fetch categories when the debounced term or search type changes
    useEffect(() => {
        const term = debouncedCategorySearchTerm?.toString().trim();
        fetchCategoriesWithSearch(term, state.categorySearchType, auth.auth.token);

        // cleanup when dependency changes
        return () => {
            if (categoryAbortCtrlRef.current) categoryAbortCtrlRef.current.abort();
        };
    }, [debouncedCategorySearchTerm, state.categorySearchType, auth.auth.token, fetchCategoriesWithSearch]);

    // Effect: fetch voucher themes when the debounced term or search type changes
    useEffect(() => {
        const term = debouncedVoucherThemeSearchTerm?.toString().trim();
        fetchVoucherThemesWithSearch(term, state.voucherThemeSearchType, auth.auth.token);

        // cleanup when dependency changes
        return () => {
            if (voucherThemeAbortCtrlRef.current) voucherThemeAbortCtrlRef.current.abort();
        };
    }, [debouncedVoucherThemeSearchTerm, state.voucherThemeSearchType, auth.auth.token, fetchVoucherThemesWithSearch]);


    useEffect(() => {
        const term = debouncedClientSearchTerm?.toString().trim();
        fetchClientsWithSearch(term, state.clientSearchType, auth.auth.token);

        // cleanup when dependency changes
        return () => {
            if (clientAbortCtrlRef.current) clientAbortCtrlRef.current.abort();
        };
    }, [debouncedClientSearchTerm, state.clientSearchType, auth.auth.token, fetchClientsWithSearch]);

    useEffect(() => {
        fetchAddOns();
    }, [fetchAddOns]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setState(prev => ({
            ...prev,
            formData: {
                ...prev.formData,
                [name]: value
            }
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // run JS validation
        const validationErrors = validateForm(state.formData);
        if (Object.keys(validationErrors).length > 0) {
            setState(prevState => ({
                ...prevState,
                fieldErrors: validationErrors
            }));
            e.stopPropagation();
            return;
        }

        // clear errors and proceed
        setState(prevState => ({
            ...prevState,
            fieldErrors: {}
        }));

        setState(prevState => ({ ...prevState, loading: true, error: '' }));

        try {
            const payload = buildPayload(state.formData);
            // ensure numeric conversions where necessary
            if (payload.clientId !== null && typeof payload.clientId === 'string') {
                const n = Number(payload.clientId);
                payload.clientId = Number.isNaN(n) ? null : n;
            }

            if (payload.productCategoryId !== null && typeof payload.productCategoryId === 'string') {
                const n = Number(payload.productCategoryId);
                payload.productCategoryId = Number.isNaN(n) ? null : n;
            }

            // If backend expects addOnIds as numbers, ensure that
            payload.addOnIds = Array.isArray(payload.addOnIds) ? payload.addOnIds.map(Number) : [];

            const response = await addProduct(payload, auth.auth.token);

            if (response) {
                toast.success('Product Added Successfully!');

                // Reset formData to the shared initial object so all fields (including slug) are cleared
                setState(prevState => ({
                    ...prevState,
                    formData: { ...INITIAL_FORM_DATA },
                    fieldErrors: {},
                    validated: false
                }));
            } else {
                toast.error('Failed to create product: ' + response.message);
            }

        } catch (err) {
            setState(prevState => ({ ...prevState, error: 'Failed to create product. Please try again.' }));
            toast.error('Failed to create product');
        } finally {
            setState(prevState => ({ ...prevState, loading: false }));
        }
    };

    return (
        <div className={styles.container}>
            <PageHeader text="Add Products" />

            <Card className={styles.formCard}>
                <Card.Body>
                    <Form noValidate onSubmit={handleSubmit}>
                        {state.error && (
                            <Alert variant="danger" className="mb-4">
                                {state.error}
                            </Alert>
                        )}

                        <CategorySelect
                            categories={state.categories}
                            value={state.formData.selectedCategoryId}
                            onChange={handleInputChange}
                            error={state.fieldErrors.selectedCategoryId}
                            loading={state.loadingCategories}
                            searchType={state.categorySearchType}
                            setSearchType={(name) => setState({ categorySearchType: name })}
                            searchTerm={state.categorySearchTerm}
                            setSearchTerm={(name) => {
                                setState({ categorySearchTerm: name });
                                // the effect watching debouncedCategorySearchTerm will handle fetching
                            }}
                        />
                        <ClientSelect
                            clients={state.clients}
                            value={state.formData.clientId}
                            onChange={handleInputChange}
                            error={state.fieldErrors.clientId}
                            loading={state.loadingClients}
                            searchType={state.clientSearchType}
                            setSearchType={(name) => setState({ clientSearchType: name })}
                            searchTerm={state.clientSearchTerm}
                            setSearchTerm={(name) => {
                                setState({ clientSearchTerm: name });
                                // the effect watching debouncedClientSearchTerm will handle fetching
                            }}
                        />
                        <VoucherThemeSelect
                            voucherThemes={state.voucherThemes}
                            // single-select mode (no chips)
                            edit
                            value={state.formData.selectedVoucherThemes}
                            onChange={handleInputChange}
                            error={state.fieldErrors.selectedVoucherThemes}
                            loading={state.loadingVoucherThemes}
                            searchType={state.voucherThemeSearchType}
                            setSearchType={(name) => setState({ voucherThemeSearchType: name })}
                            searchTerm={state.voucherThemeSearchTerm}
                            setSearchTerm={(name) => {
                                setState({ voucherThemeSearchTerm: name });
                                // the effect watching debouncedVoucherThemeSearchTerm will handle fetching
                            }}
                        />
                        <AddOnsSelect
                            addOns={state.addOns}
                            value={state.formData.selectedAddOns}
                            onChange={handleInputChange}
                            error={state.fieldErrors.selectedAddOns}
                        />
                        <MerchantGroupSelect
                            value={state.formData.selectedMerchantGroupId}
                            onChange={handleInputChange}
                            merchantGroups={state.merchantGroups}
                            loading={state.loadingMerchantGroups}
                            searchType={state.merchantGroupSearchType}
                            setSearchType={(name) => setState({ merchantGroupSearchType: name })}
                            searchTerm={state.merchantGroupSearchTerm}
                            setSearchTerm={(name) => {
                                setState({ merchantGroupSearchTerm: name });
                                // the effect watching debouncedMerchantGroupSearchTerm will handle fetching
                            }}
                            // parent handles debounced fetching
                            error={state.fieldErrors.selectedMerchantGroupId}
                        />
                        {FORM_FIELDS.map(({ name, label, type, placeholder, required, rows }) => (
                            <Form.Group as={Row} className="mb-3" key={name}>
                                <Form.Label column sm={3}>
                                    {label} {required && <span className="text-danger">*</span>}
                                </Form.Label>
                                <Col sm={9}>
                                    <Form.Control
                                        as={type === 'textarea' ? type : 'input'}
                                        type={type !== 'textarea' ? type : undefined}
                                        name={name}
                                        value={state.formData[name]}
                                        onChange={handleInputChange}
                                        placeholder={placeholder}
                                        rows={rows}
                                        isInvalid={!!state.fieldErrors[name]}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {state.fieldErrors[name] || (required ? 'This field is required' : '')}
                                    </Form.Control.Feedback>
                                </Col>
                            </Form.Group>
                        ))}

                        <OptionsSection
                            label="Delivery Options"
                            options={[
                                { id: 'emailDelivery', name: 'emailDelivery', label: 'Email', checked: state.formData.emailDelivery },
                                { id: 'smsDelivery', name: 'smsDelivery', label: 'SMS', checked: state.formData.smsDelivery }
                            ]}
                            onToggle={(name, checked) => handleInputChange({ target: { name, value: checked } })}
                        />


                        <OptionsSection
                            label="Product Options"
                            options={[
                                { id: 'isFeatured', name: 'isFeatured', label: 'Is this a featured product?', checked: state.formData.isFeatured },
                                { id: 'isPublished', name: 'isPublished', label: 'Do you want to publish this product in customer portal?', checked: state.formData.isPublished }
                            ]}
                            onToggle={(name, checked) => handleInputChange({ target: { name, value: checked } })}
                        />


                        <div className={styles.buttonContainer}>
                            <Button
                                variant="primary"
                                type="submit"
                                disabled={state.loading}
                            >
                                {state.loading ? 'Adding...' : 'Add Product'}
                            </Button>
                        </div>
                    </Form>
                </Card.Body>
            </Card >
        </div >
    );
}

const mapStateToProps = (state) => ({
    auth: state.auth,
});

export default connect(mapStateToProps)(AddProducts);
